import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { RouteMap } from "@/components/RouteMap";
import { Cesium3DMap } from "@/components/Cesium3DMap";
import { getAuthOptions } from "@/api/auth";
import { PLANNER_CONFIGS, type PlannerCriteria } from "@/lib/ai-mocks";

export function AiPlannerPage() {
  const [criteria, setCriteria] = useState<PlannerCriteria>("faster");
  const [loading, setLoading] = useState(false);
  const [loaderCopy, setLoaderCopy] = useState(
    "Route model synced. Mock state is idle.",
  );
  const [status, setStatus] = useState(
    "Mock action only. Planner is not connected to VBS.",
  );
  const [busy, setBusy] = useState(false);

  const state = PLANNER_CONFIGS[criteria];
  const authOptionsQ = useQuery({
    queryKey: ["auth", "options"],
    queryFn: getAuthOptions,
    staleTime: 5 * 60_000,
  });
  const ionToken = authOptionsQ.data?.cesium_ion_token ?? null;

  const routes = useMemo(
    () => [{ path: state.points.map((p) => [p.latitude, p.longitude]), color: state.color, points: state.points }],
    [state],
  );

  function handleCriteriaChange(next: PlannerCriteria) {
    setCriteria(next);
    setLoading(true);
    setLoaderCopy(`Recomputing ${next} route in mock mode...`);
    window.setTimeout(() => {
      setLoading(false);
      setLoaderCopy(`${PLANNER_CONFIGS[next].title} loaded from mocked planner state.`);
    }, 1100);
  }

  function runVbs() {
    setBusy(true);
    setStatus("Mock VBS launch in progress...");
    window.setTimeout(() => {
      setBusy(false);
      setStatus("Mock complete. No real VBS process was started.");
    }, 1500);
  }

  return (
    <div className="mock-page" id="ai-planner-page">
      <section className="mock-hero">
        <div className="mock-hero-copy">
          <div className="mock-badges">
            <span className="mock-badge mock-badge-hot">MOCKUP</span>
            <span className="mock-badge">SIMULATED ROUTE ENGINE</span>
          </div>
          <p className="eyebrow">Mission path prototyping</p>
          <h1>AI Planner</h1>
          <p className="mock-lede">
            Mocked route-planning view with 2D and 3D presentation, synthetic obstacles, and fake optimization
            criteria for faster, most optimal, and most quiet flight plans.
          </p>
        </div>
        <div className="mock-hero-actions">
          <button
            className="button primary mock-run-button"
            type="button"
            onClick={runVbs}
            disabled={busy}
          >
            {busy ? "Run VBS (mock loading...)" : "Run VBS"}
          </button>
          <span className="mock-run-status">{status}</span>
        </div>
      </section>

      <section className="mock-warning-strip">
        <strong>Mock planner surface.</strong>
        <span>All routes, circles, ETAs, and optimization labels are simulated for UI review only.</span>
      </section>

      <section className="panel mock-panel mock-watermarked">
        <div className="panel-head">
          <h2>Optimization controls</h2>
          <p>Switching criteria triggers a fake recalculation loader</p>
        </div>
        <div className="mock-toolbar">
          <label className="perf-filter mock-inline-filter">
            <span>Criteria</span>
            <select
              value={criteria}
              onChange={(e) => handleCriteriaChange(e.target.value as PlannerCriteria)}
            >
              <option value="faster">Faster</option>
              <option value="optimal">Most optimal</option>
              <option value="quiet">Most quiet</option>
            </select>
          </label>
          <div className="mock-loading-indicator" aria-live="polite">
            {loaderCopy}
          </div>
        </div>
      </section>

      <section className="mock-grid mock-grid-planner">
        <article className="mock-panel mock-watermarked">
          <div className="mock-surface-wrap">
            {loading && (
              <div className="mock-loading-overlay is-visible">
                <div className="mock-spinner" />
                <span>Recomputing mocked route...</span>
              </div>
            )}
            <div className="replay-surface-area replay-layout-split mock-replay-surface-area">
              <article className="surface-panel replay-2d-panel mock-map-panel">
                <header>
                  <h3>2D trajectory</h3>
                  <p>Mocked Leaflet view with the selected planner route.</p>
                </header>
                <div className="map-surface compare-map-surface">
                  <RouteMap
                    routes={routes}
                    structures={state.structures}
                    homePoint={state.homePoint}
                    height={360}
                  />
                </div>
              </article>
              <article className="surface-panel replay-3d-panel mock-map-panel">
                <header className="surface-panel-header">
                  <div>
                    <h3>3D terrain replay</h3>
                    <p>Cesium terrain render of the mocked planner route.</p>
                  </div>
                </header>
                <div className="map-surface compare-map-surface compare-map-surface-3d">
                  <Cesium3DMap
                    points={state.points}
                    color={state.color}
                    structures={state.structures}
                    homePoint={state.homePoint}
                    ionToken={ionToken}
                    height={360}
                  />
                </div>
              </article>
            </div>
            <div className="mock-planner-caption">
              <strong>{state.title}</strong>
              <span>{state.copy}</span>
            </div>
          </div>
        </article>

        <aside className="mock-stack">
          <article className="panel mock-panel mock-watermarked">
            <div className="panel-head">
              <h2>Mock route stats</h2>
              <p>Metrics change with the selected planner mode</p>
            </div>
            <div className="mock-kv-list">
              <div>
                <span>ETA</span>
                <strong>{state.eta}</strong>
              </div>
              <div>
                <span>Energy draw</span>
                <strong>{state.energy}</strong>
              </div>
              <div>
                <span>Noise envelope</span>
                <strong>{state.noise}</strong>
              </div>
              <div>
                <span>Risk score</span>
                <strong>{state.risk}</strong>
              </div>
            </div>
          </article>

          <article className="panel mock-panel mock-watermarked">
            <div className="panel-head">
              <h2>Mock rationale</h2>
              <p>Why the simulated planner prefers this route</p>
            </div>
            <p className="mock-recommendation-copy">{state.rationale}</p>
          </article>
        </aside>
      </section>
    </div>
  );
}
